'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import { FiMaximize2, FiActivity } from 'react-icons/fi';
import { motion } from 'framer-motion';

interface ImageItem {
  src: string;
  alt?: string;
}

interface ScreenshotGridProps {
  images: ImageItem[];
}

const ScreenshotGrid: React.FC<ScreenshotGridProps> = ({ images }) => {
  const [index, setIndex] = useState<number>(-1);

  return (
    <div className="my-16 space-y-4">
      {/* Header for the Grid */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-indigo-500/10 rounded-lg">
          <FiActivity className="text-indigo-500 animate-pulse" size={16} />
        </div>
        <span className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-400">
          Visual_Telemetry_Archive
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {images.map((img, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            onClick={() => setIndex(i)}
            className="group relative cursor-zoom-in rounded-[2rem] overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-zinc-100 dark:bg-zinc-900 shadow-sm transition-all duration-500 hover:border-indigo-500/50 hover:shadow-2xl hover:shadow-indigo-500/10"
          >
            {/* Image Container */}
            <div className="relative w-full h-0 pb-[75%] overflow-hidden bg-zinc-50 dark:bg-zinc-900">
              <Image
                src={img.src}
                alt={img.alt || ''}
                fill
                className="object-contain object-center transition-transform duration-700 ease-in-out group-hover:scale-105"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              />

              {/* Tactical Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                  <div className="space-y-1">
                    <p className="text-[8px] font-mono text-indigo-400 uppercase tracking-widest font-bold">
                      IMG_NODE_{i.toString().padStart(2, '0')}
                    </p>
                    <p className="text-xs font-bold text-white truncate max-w-[150px]">
                      {img.alt || 'View Full Signal'}
                    </p>
                  </div>
                  <div className="p-3 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 text-white">
                    <FiMaximize2 size={14} />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <Lightbox
        open={index >= 0}
        close={() => setIndex(-1)}
        index={index}
        slides={images.map((img) => ({ 
          src: img.src, 
          title: img.alt,
          description: "Technical Analysis Visualization" 
        }))}
        plugins={[Zoom, Captions]}
        animation={{ fade: 300 }}
        controller={{ closeOnBackdropClick: true }}
        styles={{
          container: { backgroundColor: "rgba(0, 0, 0, 0.95)" },
          root: { "--yarl__color_backdrop": "rgba(0, 0, 0, 0.95)" },
        }}
      />
    </div>
  );
};

export default ScreenshotGrid;